import SaveIcon from "@mui/icons-material/Save";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  useTheme,
} from "@mui/material";
import React, {
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { marketplaceContext } from "../../contexts/MarketplaceContext/index";
import useListNft from "../../hooks/useListNft";
import { setAssets } from "../../store/reducers/asset";
import { saveState } from "../../utils/localStorage.utils";
import { LocalStorageKeys } from "../../utils/strings.utils";
import "./index.css";

interface Props {
  handleClose: () => void;
  list?: any;
  assetstatus: any;
}

const Form: React.FC<Props & { ref?: React.Ref<any> }> = forwardRef(
  (props, ref) => {
    const { handleClose, list, assetstatus } = props;
    const { t } = useTranslation();
    const today = new Date();
    const nextweek = new Date(today);
    const [price, setPrice] = useState(0);
    const [duration, setDuration] = useState("7");
    const [isProcessing, setIsProcessing] = useState(false);
    const theme = useTheme();
    const { currencyList, chainId } = useContext(marketplaceContext);
    const [openalert, setOpenalert] = React.useState(false);
    const [alertString, setAlertString] = useState("");
    const [switching, setSwitching] = React.useState(false);
    const isMountedRef = useRef(false);
    const chainIdError = useRef(false);

    const [copytext, setCopytext] = useState("");
    const copyToClipboard = (source: string) => {
      const copiedText = document.createElement("input");
      document.body.appendChild(copiedText);
      copiedText.setAttribute("value", source);
      copiedText.select();
      document.execCommand("copy");
      document.body.removeChild(copiedText);
    };
    const clickCopy = (source: string) => {
      copyToClipboard(source);
      setCopytext(t("Message_Item_Copytext"));
      setTimeout(() => {
        setCopytext("");
      }, 1300);
    };

    const handleError = () => {
      setOpenalert(true);
      setTimeout(handleAlert, 5000);
    };
    const handleAlert = () => {
      setOpenalert(false);
    };
    const date = nextweek.setDate(today.getDate() + Number(duration));
    const [expired, setExpired] = useState(
      `${new Date(date).getFullYear()}-${`${
        new Date(date).getMonth() + 1
      }`.padStart(2, "0")}-${`${new Date(date).getDate()}`.padStart(
        2,
        "0"
      )}T${`${new Date(date).getHours()}`.padStart(2, "0")}:${`${new Date(
        date
      ).getMinutes()}`.padStart(2, "0")}`
    );
    const [currency, setCurrency] = useState(currencyList.ETH_CONTRACT);
    const [isApprove, setIsApprove] = useState(false);
    const {
      approveERC721,
      isApproveForAllERC721,
      checkChainId,
      checkERC721owner,
      checkERC1155owner,
      checkAssetChainId,
    } = useListNft();
    console.log(checkAssetChainId(assetstatus));
    const handleSubmit = async (e: React.SyntheticEvent) => {
      setIsProcessing(true);
      e.preventDefault();
      try {
        await list(price, currency, expired);
        handleClose();
      } catch (err) {
        setAlertString(t("List_Nft_List_Fail"));
        handleError();
      } finally {
        setIsProcessing(false);
      }
    };
    const handleApprove = async (e: React.SyntheticEvent) => {
      setIsProcessing(true);
      e.preventDefault();
      console.log("approve loading start");
      try {
        let transaction = await approveERC721(assetstatus.assetContract);
        const transactionReceipt = await transaction.wait();
        if (transactionReceipt.status == 1) {
          let result = await isApproveForAllERC721(assetstatus.assetContract);
          if (result.detail === true) {
            setIsApprove(true);
          }
        } else {
          console.log("approve tx error");
        }
      } catch (err) {
        console.log("approve tx error");
        setAlertString(t("List_Nft_Approve_Fail"));
        handleError();
      } finally {
        setIsProcessing(false);
        console.log("approve loading end");
      }
    };
    const handleSwitch = async (e: React.SyntheticEvent) => {
      setIsProcessing(true);
      e.preventDefault();
      try {
        await checkChainId(assetstatus);
      } catch (err) {
        setAlertString(t("List_Nft_Switch_Fail"));
        handleError();
        console.log(err);
      } finally {
        setIsProcessing(false);
      }
    };

    useEffect(() => {
      (async () => {
        if (parseInt(checkAssetChainId(assetstatus)) == chainId) {
          let result = await isApproveForAllERC721(assetstatus.assetContract);
          setIsApprove(result.detail);
        }
      })();
    }, [assetstatus.assetContract, chainId]);

    useEffect(() => {
      if (duration === "custom") return;
      const newdate = new Date(today);
      const date = newdate.setDate(today.getDate() + Number(duration));
      setExpired(
        `${new Date(date).getFullYear()}-${`${
          new Date(date).getMonth() + 1
        }`.padStart(2, "0")}-${`${new Date(date).getDate()}`.padStart(
          2,
          "0"
        )}T${`${new Date(date).getHours()}`.padStart(2, "0")}:${`${new Date(
          date
        ).getMinutes()}`.padStart(2, "0")}`
      );
    }, [duration]);
    useEffect(() => {
      (async () => {
        try {
          if (isMountedRef.current == false) {
            setSwitching(true);
            isMountedRef.current = true;
            await checkChainId(assetstatus);
            let owner;
            if (assetstatus.itemType == "ERC721") {
              owner = await checkERC721owner(
                assetstatus.assetContract,
                assetstatus.tokenId
              );
            } else {
              owner = await checkERC1155owner(
                assetstatus.assetContract,
                assetstatus.tokenId
              );
            }
            if (owner.detail == false) {
              saveState(LocalStorageKeys.ASSET_LIST, {});
              saveState(LocalStorageKeys.ASSET_LAST_UPDATE_TIME, new Date());
              setAssets({});
            }
            let result = await isApproveForAllERC721(assetstatus.assetContract);
            setIsApprove(result.detail);
            setSwitching(false);
            isMountedRef.current = false;
          }
        } catch (err) {
          setSwitching(false);
          isMountedRef.current = false;
        }
      })();
    }, []);
    useEffect(() => {
      if (parseInt(checkAssetChainId(assetstatus)) != chainId) {
        chainIdError.current = true;
        setAlertString(t("List_Nft_Chain_Id_Error"));
        setOpenalert(true);
      } else {
        chainIdError.current = false;
        handleAlert();
      }
    }, [chainId]);
    return (
      <form
        ref={ref}
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "2rem",
          overflow: "scroll",
        }}
      >
        <Snackbar
          open={openalert}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            variant="filled"
            severity="error"
            color="error"
            onClose={handleAlert}
          >
            <strong>{alertString}</strong>
          </Alert>
        </Snackbar>
        <div className="nft-list-modal-card">
          <Tooltip
            title={
              t("List_Nft_Visit_NFT") + " " + assetstatus.permalink + copytext
            }
            placement="bottom-start"
            disableInteractive
            followCursor
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  width: "210px",
                },
              },
            }}
          >
            <div
              className="nft-list-modal-card-img"
              onClick={() => {
                clickCopy(assetstatus.permalink);
              }}
            >
              <img src={assetstatus.imageUrl} />
            </div>
          </Tooltip>
          <div className="nft-list-modal-card-info">
            <div className="nft-list-modal-card-info-price">
              <TextField
                label={t("List_Nft_Price")}
                variant="standard"
                type="number"
                required
                value={price}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPrice(Number(e.target.value))
                }
                error={price <= 0}
                sx={{ m: 1, maxWidth: 80, height: 50 }}
                size="small"
              />
              <FormControl
                variant="standard"
                sx={{ m: 1, minWidth: 80, height: 50 }}
              >
                <InputLabel id="currency-select-standard-label">
                  {t("List_Nft_Currency")}
                </InputLabel>
                {chainId == 1 || 5 ? (
                  <Select
                    labelId="urrency-select-standard-label"
                    id="currency-select-standard"
                    value={currency}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setCurrency(e.target.value)
                    }
                    label="Currency"
                    size="small"
                  >
                    <MenuItem value={currencyList.ETH_CONTRACT}>ETH</MenuItem>
                    <MenuItem value={currencyList.USDC_CONTRACT}>USDC</MenuItem>
                    <MenuItem value={currencyList.USDT_CONTRACT}>USDT</MenuItem>
                  </Select>
                ) : chainId == 137 ? (
                  <Select
                    labelId="urrency-select-standard-label"
                    id="currency-select-standard"
                    value={currency}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setCurrency(e.target.value)
                    }
                    label="Currency"
                    size="small"
                  >
                    <MenuItem value={currencyList.ETH_CONTRACT}>ETH</MenuItem>
                    <MenuItem value={currencyList.USDC_CONTRACT}>USDC</MenuItem>
                    <MenuItem value={currencyList.USDT_CONTRACT}>USDT</MenuItem>
                    <MenuItem value={currencyList.MARKET_CONTRACT}>
                      MATIC
                    </MenuItem>
                  </Select>
                ) : (
                  <Select
                    labelId="urrency-select-standard-label"
                    id="currency-select-standard"
                    value={currency}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setCurrency(e.target.value)
                    }
                    label="Currency"
                    size="small"
                  ></Select>
                )}
              </FormControl>
              <FormControl>
                <FormLabel id="demo-row-radio-buttons-group-label">
                  {t("List_Nft_Duration")}
                </FormLabel>
                <RadioGroup
                  row
                  name="set-duration-buttons-group"
                  value={duration}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setDuration(e.target.value)
                  }
                  style={{ marginLeft: "16px" }}
                >
                  <FormControlLabel
                    value="7"
                    control={<Radio size="small" />}
                    label={t("List_Nft_Duration_7")}
                  />
                  <FormControlLabel
                    value="30"
                    control={<Radio size="small" />}
                    label={t("List_Nft_Duration_30")}
                  />
                  <FormControlLabel
                    value="custom"
                    control={<Radio size="small" />}
                    label={t("List_Nft_Duration_Custom")}
                  />
                </RadioGroup>
              </FormControl>
              <TextField
                id="Expired"
                label={t("List_Nft_Expired_Date")}
                type="datetime-local"
                variant="standard"
                value={expired}
                sx={{ width: 200, m: 1, fontSize: 5 }}
                InputLabelProps={{
                  shrink: true,
                }}
                error={new Date(expired).getTime() < new Date().getTime()}
                inputProps={{
                  min: new Date().toISOString().slice(0, 16),
                  readOnly: duration !== "custom",
                }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  console.log("Expired", e.target.value);
                  setExpired(e.target.value);
                }}
                size="small"
              />
            </div>
          </div>
        </div>
        <div className="nft-list-modal-buttons">
          {switching ? (
            <LoadingButton
              color="secondary"
              loading={true}
              loadingPosition="start"
              startIcon={<SaveIcon />}
              variant="contained"
            >
              <span>{t("List_Nft_Switching")}</span>
            </LoadingButton>
          ) : chainIdError.current ? (
            <LoadingButton
              className="nft-buy-modal-button-buy"
              onClick={handleSwitch}
              loading={isProcessing}
              variant="contained"
              sx={{ backgroundColor: theme.brandColor, color: "#fff" }}
            >
              {t("List_Nft_Button_SWITCH")}
            </LoadingButton>
          ) : isApprove ? (
            <LoadingButton
              disabled={
                price <= 0 || new Date(expired).getTime() < new Date().getTime()
              }
              className="nft-list-modal-button-buy"
              onClick={handleSubmit}
              loading={isProcessing}
              variant="contained"
              sx={{ backgroundColor: theme.brandColor, color: "#fff" }}
            >
              {t("List_Nft_List")}
            </LoadingButton>
          ) : (
            <LoadingButton
              className="nft-list-modal-button-buy"
              onClick={handleApprove}
              loading={isProcessing}
              variant="contained"
              sx={{ backgroundColor: theme.brandColor, color: "#fff" }}
            >
              {t("List_Nft_Button_Approve")}
            </LoadingButton>
          )}

          {!switching ? (
            <button
              className="nft-buy-modal-button-cancel"
              onClick={handleClose}
            >
              {t("List_Nft_Button_Cancel")}
            </button>
          ) : (
            <></>
          )}
        </div>
      </form>
    );
  }
);
export default Form;
